import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Popover,
    Typography,
} from '@material-ui/core'
import { ADMIN_GET_EVENT_DETAIL, ADMIN_GET_EVENT_STREAM } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { formatUtcTimeAgo, formatUtcTimestamp } from 'utils/formatUtcTimestamp'

const POLL_INTERVAL_MS = 2000
const MAX_WATCH_MS = 30 * 60 * 1000

const DEPLOYMENT_KINDS = [
    'integration_started',
    'integration_completed',
    'integration_failed',
    'client_error',
    'client_event',
    'heartbeat',
]

const ERROR_KINDS = new Set([
    'integration_failed',
    'client_error',
    'exception',
])

function isEventError(event) {
    if (!event) return false
    if (event.is_error) return true
    return ERROR_KINDS.has(event.kind)
}

function kindLabel(kind) {
    if (!kind) return 'event'
    return kind.replace(/_/g, ' ')
}

function kindTone(kind) {
    if (!kind) return 'default'
    if (kind === 'integration_failed') return 'error'
    if (kind === 'integration_completed') return 'success'
    if (kind === 'integration_started') return 'running'
    if (kind === 'heartbeat') return 'heartbeat'
    if (kind === 'client_error') return 'error'
    if (kind === 'exception') return 'error'
    return 'default'
}

function eventTone(event) {
    if (isEventError(event)) return 'error'
    return kindTone(event?.kind)
}

function EventDetailPopover({ anchorEl, detail, loading, error, onClose }) {
    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ className: 'sa-result-popover' }}
        >
            <div className="sa-pop-header">
                <span className="sa-pop-title">Event detail</span>
                <button type="button" className="sa-pop-close" onClick={onClose}>✕</button>
            </div>
            <div className="sa-pop-body">
                {loading && (
                    <div className="superadmin-event-stream__detail-status">
                        <CircularProgress size={16} />
                        <span>Loading…</span>
                    </div>
                )}
                {error && (
                    <Typography variant="body2" color="error">{error}</Typography>
                )}
                {!loading && !error && detail && (
                    <>
                        <div className="superadmin-event-stream__detail-meta">
                            <Chip
                                size="small"
                                label={kindLabel(detail.kind)}
                                className={`superadmin-event-stream__chip superadmin-event-stream__chip--${eventTone(detail)}`}
                            />
                            <span>{formatUtcTimestamp(detail.timestamp)}</span>
                        </div>
                        <Typography variant="body2" className="superadmin-event-stream__detail-summary">
                            {detail.summary}
                        </Typography>
                        <pre className="sa-pop-cmd-pre">{JSON.stringify(detail.detail || detail, null, 2)}</pre>
                    </>
                )}
            </div>
        </Popover>
    )
}

export function EventStreamPanel({
    tenantId = null,
    defaultKinds = DEPLOYMENT_KINDS,
    showTenantColumn = false,
    hint,
}) {
    const [watching, setWatching] = useState(false)
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [includeCommandAudits, setIncludeCommandAudits] = useState(false)
    const [hideHeartbeats, setHideHeartbeats] = useState(false)
    const [errorsOnly, setErrorsOnly] = useState(false)

    const [detailAnchor, setDetailAnchor] = useState(null)
    const [detail, setDetail] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailError, setDetailError] = useState(null)

    const sinceRef = useRef(null)
    const pollRef = useRef(null)
    const watchStartedRef = useRef(null)
    const watchTimeoutRef = useRef(null)

    const streamKinds = hideHeartbeats
        ? defaultKinds.filter(k => k !== 'heartbeat')
        : defaultKinds

    const activeKinds = includeCommandAudits
        ? [...streamKinds, 'command_audit', 'exception']
        : streamKinds

    const heartbeatFiltered = hideHeartbeats
        ? events.filter(e => e.kind !== 'heartbeat')
        : events

    const visibleEvents = errorsOnly
        ? heartbeatFiltered.filter(isEventError)
        : heartbeatFiltered

    const stopWatching = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
        if (watchTimeoutRef.current) {
            clearTimeout(watchTimeoutRef.current)
            watchTimeoutRef.current = null
        }
        setWatching(false)
        setLoading(false)
    }, [])

    const pollEvents = useCallback(() => {
        const payload = {
            since: sinceRef.current || undefined,
            limit: 50,
            kinds: activeKinds,
        }
        if (tenantId) payload.tenant_id = tenantId

        ADMIN_GET_EVENT_STREAM(payload)
            .then(res => {
                const incoming = Array.isArray(res?.events) ? res.events : []
                if (incoming.length > 0) {
                    setEvents(prev => {
                        const seen = new Set(prev.map(e => e.id))
                        const merged = [...prev]
                        for (const event of incoming) {
                            if (!seen.has(event.id)) {
                                merged.unshift(event)
                                seen.add(event.id)
                            }
                        }
                        return merged.slice(0, 200)
                    })
                    sinceRef.current = res.cursor || incoming[0].timestamp
                }
                setError(null)
            })
            .catch(ex => {
                const errMsg = getApiErrorMessage(ex, 'Failed to load event stream')
                setError(errMsg)
            })
            .finally(() => setLoading(false))
    }, [tenantId, activeKinds])

    const startWatching = () => {
        setEvents([])
        setError(null)
        sinceRef.current = null
        watchStartedRef.current = Date.now()
        setWatching(true)
        setLoading(true)
        pollEvents()
        pollRef.current = setInterval(pollEvents, POLL_INTERVAL_MS)
        watchTimeoutRef.current = setTimeout(() => {
            stopWatching()
            setError('Event watch stopped after 30 minutes.')
        }, MAX_WATCH_MS)
    }

    const clearEvents = () => {
        setEvents([])
        sinceRef.current = null
        setError(null)
    }

    const openDetail = (event, anchorEl) => {
        setDetailAnchor(anchorEl)
        setDetail(null)
        setDetailError(null)
        setDetailLoading(true)
        ADMIN_GET_EVENT_DETAIL({ tenant_id: event.tenant_id, id: event.id })
            .then(res => {
                setDetail(res)
                setDetailError(null)
            })
            .catch(ex => {
                setDetailError(getApiErrorMessage(ex, 'Failed to load event detail'))
            })
            .finally(() => setDetailLoading(false))
    }

    useEffect(() => () => stopWatching(), [stopWatching])

    return (
        <div className="superadmin-event-stream">
            {hint && (
                <Typography variant="body2" color="textSecondary" className="superadmin-event-stream__hint">
                    {hint}
                </Typography>
            )}

            <div className="superadmin-toolbar superadmin-event-stream__toolbar">
                {!watching ? (
                    <Button variant="contained" color="primary" size="small" onClick={startWatching}>
                        Watch events
                    </Button>
                ) : (
                    <>
                        <Button variant="outlined" size="small" onClick={stopWatching}>
                            Stop watching
                        </Button>
                        <Button variant="text" size="small" onClick={clearEvents}>
                            Clear
                        </Button>
                        {loading && <CircularProgress size={16} />}
                        <Chip
                            size="small"
                            label={`${visibleEvents.length} event${visibleEvents.length === 1 ? '' : 's'}`}
                            className="superadmin-event-stream__count"
                        />
                    </>
                )}
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setHideHeartbeats(v => !v)}
                >
                    {hideHeartbeats ? 'Show heartbeats' : 'Hide heartbeats'}
                </Button>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setErrorsOnly(v => !v)}
                >
                    {errorsOnly ? 'Show all events' : 'Errors only'}
                </Button>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setIncludeCommandAudits(v => !v)}
                    disabled={!watching}
                >
                    {includeCommandAudits ? 'Hide command audits' : 'Show command audits'}
                </Button>
            </div>

            {error && (
                <Typography variant="body2" color="error" className="superadmin-muted-note">
                    {error}
                </Typography>
            )}

            {!watching && events.length === 0 && (
                <Typography variant="body2" color="textSecondary" className="superadmin-muted-note">
                    Start watching to see integration and heartbeat events as they arrive.
                </Typography>
            )}

            {watching && !loading && visibleEvents.length === 0 && !error && (
                <Typography variant="body2" color="textSecondary" className="superadmin-muted-note">
                    {tenantId
                        ? 'No events recorded for this tenant yet. Events appear after client heartbeats or integrations. Try System → Recent events for all tenants.'
                        : 'Watching for new events…'}
                </Typography>
            )}

            {visibleEvents.length > 0 && (
                <div className={`superadmin-event-stream__list${showTenantColumn ? ' superadmin-event-stream__list--with-tenant' : ''}`}>
                    {visibleEvents.map(event => (
                        <button
                            key={event.id}
                            type="button"
                            className={`superadmin-event-stream__row${isEventError(event) ? ' superadmin-event-stream__row--error' : ''}`}
                            onClick={e => openDetail(event, e.currentTarget)}
                        >
                            <span className="superadmin-event-stream__time">
                                {formatUtcTimeAgo(event.timestamp)}
                            </span>
                            <Chip
                                size="small"
                                label={kindLabel(event.kind)}
                                className={`superadmin-event-stream__chip superadmin-event-stream__chip--${eventTone(event)}`}
                            />
                            {showTenantColumn && (
                                <span className="superadmin-event-stream__tenant">
                                    {event.tenant_id}
                                </span>
                            )}
                            <span className="superadmin-event-stream__summary">{event.summary}</span>
                        </button>
                    ))}
                </div>
            )}

            <EventDetailPopover
                anchorEl={detailAnchor}
                detail={detail}
                loading={detailLoading}
                error={detailError}
                onClose={() => setDetailAnchor(null)}
            />
        </div>
    )
}
